import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '@/features/auth';
import { companyService } from '@/lib/companyService';
import { Button, Card, CardHeader, CardBody } from '@/components/ui';
import {
  Building2,
  Globe,
  FileText,
  Hash,
  MapPin,
  AlertCircle,
  CheckCircle2,
  ChevronRight,
} from 'lucide-react';

// Form data interface
interface CompanyFormData {
  legal_name: string;
  country: string;
  jurisdiction?: string;
  registration_number: string;
  tax_id: string;
  // Local fields (will be packaged into local_requirements)
  deductions_portfolio?: string; // Israel
  cra_program_account?: string;  // Canada
}

// Canadian provinces
const CANADIAN_PROVINCES = [
  'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador',
  'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island',
  'Quebec', 'Saskatchewan', 'Yukon',
];

// US states (common incorporation states)
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming',
];

export function OnboardingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // React Hook Form
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CompanyFormData>({
    defaultValues: {
      legal_name: '',
      country: '',
      jurisdiction: '',
      registration_number: '',
      tax_id: '',
      deductions_portfolio: '',
      cra_program_account: '',
    },
  });

  // Watch country to trigger progressive reveal
  const selectedCountry = watch('country');

  const onSubmit = async (data: CompanyFormData) => {
    if (!user?.id) return;

    setLoading(true);
    setError(null);

    try {
      // Build local_requirements based on country
      let localRequirements: Record<string, string> | null = null;

      if (selectedCountry === 'IL' && data.deductions_portfolio) {
        localRequirements = {
          deductions_portfolio: data.deductions_portfolio,
        };
      } else if (selectedCountry === 'CA' && data.cra_program_account) {
        localRequirements = {
          cra_program_account: data.cra_program_account,
        };
      }

      // Prepare company data
      const companyData = {
        legal_name: data.legal_name,
        country: data.country,
        jurisdiction: data.jurisdiction || null,
        registration_number: data.registration_number,
        tax_id: data.tax_id,
        lei: null,
        duns: null,
        local_requirements: localRequirements,
      };

      const { data: result, error: createError } = await companyService.createCompany(
        user.id,
        companyData
      );

      if (createError || !result) {
        console.error('Company creation failed:', createError);
        
        // Better error handling with detailed messages
        let errorMessage = 'Failed to create company. Please try again.';
        
        if (typeof createError === 'object' && createError !== null) {
          const err = createError as any;
          if (err.code === '42501') {
            errorMessage = 'Permission denied. Please ensure you are logged in and try again.';
          } else if (err.message) {
            errorMessage = `Error: ${err.message}`;
          } else if (err.error_description) {
            errorMessage = err.error_description;
          }
        } else if (typeof createError === 'string') {
          errorMessage = createError;
        }
        
        setError(errorMessage);
      } else {
        // Success! Redirect to dashboard
        navigate('/app');
      }
    } catch (err) {
      console.error('Unexpected error in onSubmit:', err);
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 flex items-center justify-center p-4">
      <div className="w-full max-w-3xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-2xl mb-4 shadow-lg">
            <Building2 className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome to Axelio
          </h1>
          <p className="text-gray-600">
            Let's get started by registering your business
          </p>
        </div>

        {/* Onboarding Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-gray-600" />
              <h2 className="text-xl font-semibold text-gray-900">
                Company Registration
              </h2>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Complete the form below to register your company
            </p>
          </CardHeader>

          <CardBody>
            {/* Error Alert */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 animate-fade-in">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800">
                    Registration Failed
                  </p>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* STEP 1: Core Identification - Always Visible */}
              <div className="space-y-6">
                <div className="flex items-center gap-2 text-primary-600">
                  <ChevronRight className="w-5 h-5" />
                  <h3 className="text-sm font-semibold uppercase tracking-wider">
                    Step 1: Company Identification
                  </h3>
                </div>

                {/* Legal Name */}
                <div>
                  <label
                    htmlFor="legal_name"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Legal Company Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      id="legal_name"
                      type="text"
                      {...register('legal_name', {
                        required: 'Legal company name is required',
                      })}
                      placeholder="Acme Corporation Inc."
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                    />
                  </div>
                  {errors.legal_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.legal_name.message}</p>
                  )}
                </div>

                {/* Country */}
                <div>
                  <label
                    htmlFor="country"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Country of Incorporation <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <select
                      id="country"
                      {...register('country', {
                        required: 'Please select a country',
                      })}
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all appearance-none bg-white"
                    >
                      <option value="">Select Country</option>
                      <option value="IL">Israel</option>
                      <option value="CA">Canada</option>
                      <option value="US">United States</option>
                    </select>
                  </div>
                  {errors.country && (
                    <p className="mt-1 text-sm text-red-600">{errors.country.message}</p>
                  )}
                  {!selectedCountry && (
                    <p className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Select a country to continue with registration details
                    </p>
                  )}
                </div>
              </div>

              {/* STEP 2: Progressive Reveal - Country-Specific Fields */}
              {selectedCountry && (
                <div className="space-y-6 animate-fade-in animate-slide-in-from-top">
                  <div className="pt-6 border-t border-gray-200">
                    <div className="flex items-center gap-2 text-primary-600 mb-6">
                      <ChevronRight className="w-5 h-5" />
                      <h3 className="text-sm font-semibold uppercase tracking-wider">
                        Step 2: Registration Details
                        {selectedCountry === 'IL' && ' - Israel'}
                        {selectedCountry === 'CA' && ' - Canada'}
                        {selectedCountry === 'US' && ' - United States'}
                      </h3>
                    </div>

                    {/* ISRAEL (IL) */}
                    {selectedCountry === 'IL' && (
                      <div className="space-y-6">
                        {/* Company Number */}
                        <div>
                          <label
                            htmlFor="registration_number"
                            className="block text-sm font-semibold text-gray-700 mb-2"
                          >
                            Company Number (ח.פ / ח.צ) <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                              id="registration_number"
                              type="text"
                              {...register('registration_number', {
                                required: 'Company number is required',
                              })}
                              placeholder="514123456"
                              className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                            />
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            Israeli company registration number
                          </p>
                          {errors.registration_number && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.registration_number.message}
                            </p>
                          )}
                        </div>

                        {/* VAT Number */}
                        <div>
                          <label
                            htmlFor="tax_id"
                            className="block text-sm font-semibold text-gray-700 mb-2"
                          >
                            VAT Number (עוסק מורשה) <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                              id="tax_id"
                              type="text"
                              {...register('tax_id', {
                                required: 'VAT number is required',
                              })}
                              placeholder="123456789"
                              className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                            />
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            Israeli VAT registration number
                          </p>
                          {errors.tax_id && (
                            <p className="mt-1 text-sm text-red-600">{errors.tax_id.message}</p>
                          )}
                        </div>

                        {/* Deductions Portfolio (Optional) */}
                        <div>
                          <label
                            htmlFor="deductions_portfolio"
                            className="block text-sm font-semibold text-gray-700 mb-2"
                          >
                            Deductions Portfolio (תיק ניכויים)
                          </label>
                          <div className="relative">
                            <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                              id="deductions_portfolio"
                              type="text"
                              {...register('deductions_portfolio')}
                              placeholder="1234567"
                              className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                            />
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            Israeli tax deductions portfolio number (optional)
                          </p>
                        </div>
                      </div>
                    )}

                    {/* CANADA (CA) */}
                    {selectedCountry === 'CA' && (
                      <div className="space-y-6">
                        {/* Province of Incorporation */}
                        <div>
                          <label
                            htmlFor="jurisdiction"
                            className="block text-sm font-semibold text-gray-700 mb-2"
                          >
                            Province of Incorporation <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <select
                              id="jurisdiction"
                              {...register('jurisdiction', {
                                required: 'Province is required',
                              })}
                              className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all appearance-none bg-white"
                            >
                              <option value="">Select Province</option>
                              {CANADIAN_PROVINCES.map((province) => (
                                <option key={province} value={province}>
                                  {province}
                                </option>
                              ))}
                            </select>
                          </div>
                          {errors.jurisdiction && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.jurisdiction.message}
                            </p>
                          )}
                        </div>

                        {/* Incorporation Number */}
                        <div>
                          <label
                            htmlFor="registration_number"
                            className="block text-sm font-semibold text-gray-700 mb-2"
                          >
                            Incorporation Number <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                              id="registration_number"
                              type="text"
                              {...register('registration_number', {
                                required: 'Incorporation number is required',
                              })}
                              placeholder="123456789"
                              className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                            />
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            Federal or provincial incorporation number
                          </p>
                          {errors.registration_number && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.registration_number.message}
                            </p>
                          )}
                        </div>

                        {/* Business Number (BN) */}
                        <div>
                          <label
                            htmlFor="tax_id"
                            className="block text-sm font-semibold text-gray-700 mb-2"
                          >
                            Business Number (BN) <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                              id="tax_id"
                              type="text"
                              {...register('tax_id', {
                                required: 'Business number is required',
                              })}
                              placeholder="123456789RC0001"
                              className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                            />
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            9-digit BN with program account identifier
                          </p>
                          {errors.tax_id && (
                            <p className="mt-1 text-sm text-red-600">{errors.tax_id.message}</p>
                          )}
                        </div>

                        {/* CRA Program Account (Optional) */}
                        <div>
                          <label
                            htmlFor="cra_program_account"
                            className="block text-sm font-semibold text-gray-700 mb-2"
                          >
                            CRA Program Account
                          </label>
                          <div className="relative">
                            <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                              id="cra_program_account"
                              type="text"
                              {...register('cra_program_account')}
                              placeholder="RT0001"
                              className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                            />
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            e.g., RT0001 for GST/HST, RP0001 for Payroll (optional)
                          </p>
                        </div>
                      </div>
                    )}

                    {/* UNITED STATES (US) */}
                    {selectedCountry === 'US' && (
                      <div className="space-y-6">
                        {/* State of Incorporation */}
                        <div>
                          <label
                            htmlFor="jurisdiction"
                            className="block text-sm font-semibold text-gray-700 mb-2"
                          >
                            State of Incorporation <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <select
                              id="jurisdiction"
                              {...register('jurisdiction', {
                                required: 'State is required',
                              })}
                              className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all appearance-none bg-white"
                            >
                              <option value="">Select State</option>
                              {US_STATES.map((state) => (
                                <option key={state} value={state}>
                                  {state}
                                </option>
                              ))}
                            </select>
                          </div>
                          {errors.jurisdiction && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.jurisdiction.message}
                            </p>
                          )}
                        </div>

                        {/* State File Number */}
                        <div>
                          <label
                            htmlFor="registration_number"
                            className="block text-sm font-semibold text-gray-700 mb-2"
                          >
                            State File Number <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                              id="registration_number"
                              type="text"
                              {...register('registration_number', {
                                required: 'State file number is required',
                              })}
                              placeholder="202312345678"
                              className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                            />
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            State-issued business registration number
                          </p>
                          {errors.registration_number && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.registration_number.message}
                            </p>
                          )}
                        </div>

                        {/* EIN */}
                        <div>
                          <label
                            htmlFor="tax_id"
                            className="block text-sm font-semibold text-gray-700 mb-2"
                          >
                            EIN (Employer Identification Number){' '}
                            <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                              id="tax_id"
                              type="text"
                              {...register('tax_id', {
                                required: 'EIN is required',
                              })}
                              placeholder="12-3456789"
                              className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                            />
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            9-digit IRS employer identification number
                          </p>
                          {errors.tax_id && (
                            <p className="mt-1 text-sm text-red-600">{errors.tax_id.message}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div className="pt-6 border-t border-gray-200">
                    <Button
                      type="submit"
                      isLoading={loading}
                      disabled={loading}
                      className="w-full py-3 text-base font-semibold"
                    >
                      {loading ? 'Creating Company...' : 'Complete Registration'}
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </CardBody>
        </Card>

        {/* Footer Info */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500 flex items-center justify-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            <span>Your information is encrypted and secure</span>
          </p>
        </div>
      </div>
    </div>
  );
}
